import type { ComponentProps, ReactNode } from 'react';
import { FieldInfo, hasFieldError, useFieldContext, useIsFieldOptional } from './shared';
import { useFormReadOnly } from './form-read-only';
import { Input } from '../input';
import { cn } from '../../lib/utils';
import { FieldLabelRow } from './field-label-row';

export default function TextField({
	label,
	className,
	orientation,
	description,
	optional,
	info,
	fieldSize,
	...props
}: ComponentProps<'input'> & {
	label?: string;
	orientation?: 'vertical' | 'horizontal';
	variant?: 'default' | 'outline' | 'background';
	description?: ReactNode;
	optional?: boolean;
	info?: ReactNode;
	fieldSize?: 'default' | 'lg';
}) {
	const field = useFieldContext<string>();
	const formReadOnly = useFormReadOnly();
	const isReadOnly = props.readOnly ?? formReadOnly;
	const isOptional = useIsFieldOptional(field.name, optional);
	const isHorizontal = orientation === 'horizontal';

	return (
		<div className={className}>
			<div className={isHorizontal ? 'flex items-start gap-3' : 'grid gap-2'}>
				{label && (
					<FieldLabelRow
						label={label}
						htmlFor={field.name}
						hasError={field.state.meta.isTouched && !field.state.meta.isValid}
						optional={isOptional}
						info={info}
					/>
				)}
				<div className={isHorizontal ? 'flex-1' : undefined}>
					<Input
						id={field.name}
						name={field.name}
						aria-invalid={hasFieldError(field.state.meta)}
						value={isReadOnly && !field.state.value ? '-' : (field.state.value ?? '')}
						onBlur={field.handleBlur}
						onChange={(e) => field.handleChange(e.target.value)}
						{...props}
						readOnly={isReadOnly}
						tabIndex={isReadOnly ? -1 : undefined}
						className={cn(
							fieldSize === 'lg' && 'h-12',
							isReadOnly && 'border-transparent px-0 focus-visible:ring-0 focus-visible:border-transparent',
						)}
					/>
					{description && <p className="text-muted-foreground m-2 text-xs">{description}</p>}
					<FieldInfo fieldState={field.state} />
				</div>
			</div>
		</div>
	);
}
